$neutral-50: #fafbff !default;

:host {
  display: block;
  height: 100%;
  font-family: 'Work sans', sans-serif;
  min-height: 20em;
  --table-border-color: var(--border-subtle-selected-01, $neutral-100);
  --table-border: 0.063rem solid var(--table-border-color);
  --table-col-content-padding: 0.75rem 1rem;
  --colorAccent: #e0e0e0;
  --table-border-divider: rgba(128, 128, 128, 0.349);
}

.table {
  height: 100%;
  font-size: 0.875rem;
  font-weight: 400;
  line-height: 1.125rem;
  letter-spacing: 0.01rem;
  display: flex;
  flex-direction: column;
  background: var(--layer, $light);

  .table-scroll-container {
    position: relative;
    overflow: auto;
    border-radius: 0.5rem;
    border: 0.063rem solid $neutral-100;
    height: 100%;
  }

  .empty-table {
    height: 80%;
    display: flex;
    align-items: center;
    justify-content: center;

    .empty-state {
      width: 10rem;
    }
  }

  .pagination {
    margin-top: -0.9rem;
    padding-top: 0.5rem;
    border-radius: 0.5rem;
    background: var(--layer-accent, $neutral-100);
    --input-background-color: var(--colorAccent);
    display: flex;
    border-top: 0.063rem solid var(--table-border-color);

    .form-control {
      margin: 0;
    }

    .select {
      margin: 0;
      --input-border-radius: none;
      --input-border: none;
      --input-border-inline-start: 0.063rem solid var(--table-border-color);
      --input-border-inline-end: 0.063rem solid var(--table-border-color);
    }

    .page-sizes-select {
      margin-inline-start: var(--spacing-03, 0.5rem);
    }

    .page-sizes-select select {
      margin-left: 0.625rem;
      margin-top: 0.625rem;
      font-family: 'Work Sans', sans-serif;
      margin-bottom: 0.625rem;
      border: none;
      border-radius: 0.5rem;
      width: fit-content;
      background: $neutral-100;
    }

    .pagination-item-count {
      border-left: 0.063rem solid var(--table-border-divider);
      padding-left: 0.938rem;
      margin-inline-start: var(--spacing-04, 0.75rem);
      flex: 1;
      display: flex;
      align-items: center;
    }

    .pagination-item-count h1 {
      font-size: 0.875rem;
    }

    .pagination-right {
      padding-top: 0.9rem;
      padding-right: 0.7rem;
    }

    .arrows {
      --border-radius: 0;
      --button-height: calc(2.5rem - 0.13rem);
    }
  }

  &.horizontal-scrolled {
    .fixed-columns:after {
      box-shadow: 0.625rem 0 1.3rem -1.3rem var(--border-color) inset;
      content: ' ';
      height: 100%;
      position: absolute;
      top: 0;
      right: -1.3rem;
      width: 1.3rem;
    }
  }
}

.row {
  display: flex;
  box-sizing: border-box;

  .columns-container {
    display: flex;
  }

  .col {
    margin: 0;
    color: var(--text-primary, $secondary);

    .col-content {
      display: flex;
      align-items: center;
      height: 100%;

      .col-text {
        padding: var(--table-col-content-padding);
        flex: 1;
        overflow: hidden;
        white-space: nowrap;
        text-overflow: ellipsis;
      }

      .col-template {
        width: 100%;
      }

      .col-action {
        --button-border-radius: none;

        &.has-focus {
          z-index: 12;
        }
      }

      .checkbox {
        padding: 0 var(--spacing-03, 0.5rem);
      }
    }

    &.center .col-content {
      justify-content: center;
    }

    &:last-child {
      flex: 1;
    }
  }

  .fixed-columns {
    position: sticky;
    left: 0;
    z-index: 1;
  }

  .scrollable-columns {
    flex: 1;
    /*z-index: 0;*/
  }
}

.header {
  z-index: var(--z-index-table-header, 30);
  font-weight: 600;
  font-size: 0.875rem;
  line-height: 1.125rem;
  letter-spacing: 0.01rem;
  position: sticky;
  top: 0;

  .left-panel {
    position: sticky;
    top: 0;
    left: 0;
  }

  .col {
    background: var(--layer-accent, $neutral-100);

    &.sort {
      background: var(--colorAccent);
    }
  }
}

.body {
  .row {
    .col {
      cursor: pointer;
      background: var(--layer, $light);
      border-bottom: 0.063rem solid var(--layer-accent, $neutral-100);

      &.col-checkbox .col-content {
        border: 0;
      }

      .col-content {
        border: 0.13rem solid transparent;
        border-bottom: 0.063rem solid transparent;
      }

      &:focus {
        outline: none;
        border-bottom-color: var(--color-primary, $secondary);
        z-index: 1;

        .col-content {
          border-color: var(--color-primary, $secondary);
        }
      }
    }

    &:hover {
      .col {
        background-color: var(--layer-hover, $neutral-50);
      }
    }
  }

  .left-panel {
    position: sticky;
    left: 0;
  }
}

:host(.show-full-content) .body .col-text {
  overflow: initial;
  white-space: initial;
  text-overflow: initial;
}

.sort-icon {
  border: none;
  color: $neutral-500;
  background: transparent;
  cursor: pointer;
  &:hover {
    color: $neutral-400;
  }
}

.pag-icon {
  border: none;
  background: transparent;
  cursor: pointer;
}

.empty-data {
  text-align: center;
  position: absolute;
  margin: auto;
  top: 2.875rem;
  right: 0;
  bottom: 0;
  left: 0;
  border-radius: 0.188rem;
  height: max-content;
  color: var(--color-neutral-600, $secondary);
}

.flex {
  display: flex;
}

.separator {
  background: var(--table-border-divider);
  width: 0.006rem;
  margin-left: 0.938rem;
}

.checkbox {
  margin-left: 0.938rem;
}

.actions-svg-icon {
  display: flex;
  align-items: center;
  width: 100%;
  background: $light;
  border-bottom: 0.063rem solid $neutral-100;
}
.action-icon:first-of-type {
  margin-left: 1.2rem;
}
